import './index.css'

import get from 'lodash/get'
import * as React from 'react'
import type { ConnectedProps } from 'react-redux'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'

import { TRACK } from '@disco/constants/ItemTypes'
import {
  resetTrackMergeForm,
  selectOriginalFormatInTrackMergeForm,
} from '@disco/state-management/actions/FormActions'
import { mergeTracks } from '@disco/state-management/actions/PlaylistTrackActions'
import {
  fetchTrackMetadata,
  initEditedTrack,
  updateEditedTrack,
} from '@disco/state-management/actions/ServerActions'
import { getAllSelectedItems } from '@disco/state-management/selectors/selection'
import { getHideTrackMetadataMoreButton } from '@disco/state-management/selectors/user'
import {
  getCommonBasicMetadataFields,
  getCommonExtraMetadataFields,
} from '@disco/utils/commonMetadata'
import { getListOfOriginalFormats } from '@disco/utils/tracks'

import type {
  EditableTrack,
  SelectableTrack,
  Track,
  TrackId,
} from 'types/track'

import TrackList from 'components/bulk-editor/track-list'
import HelpTooltip from 'components/common/tooltip'
import MetadataFields from 'components/common/tracks/metadata-fields'
import DocumentHeadTags from 'components/document-head-tags'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import DiscoAsyncModal from 'components/ui/disco-async-modal'
import FormNote from 'components/ui/form-note'
import { doubleScrollModalStyles } from 'components/utils/modalStyles'

import type { RootState } from 'store/configureStore'
import { getSelectionBase, getSelectionReturnPath } from 'utils/routing'

function mapStateToProps(state: RootState) {
  const selectedItems = getAllSelectedItems(state)
  return {
    selectedTracks: selectedItems.filter(
      item =>
        item.type === TRACK && !(item as any as Track).outside_your_territories,
    ),
    editedTrack: state.server.editedTrack,
    hideMoreButton: getHideTrackMetadataMoreButton(state),
    settings: state.user.settings,
    ...state.forms.trackMergeForm,
  }
}

const mapDispatchToProps = {
  mergeTracks,
  initEditedTrack,
  updateEditedTrack,
  fetchTrackMetadata,
  resetTrackMergeForm,
  selectOriginalFormatInTrackMergeForm,
}

const connector = connect(mapStateToProps, mapDispatchToProps)
type ReduxProps = ConnectedProps<typeof connector>
type Props = ReduxProps

function TrackMergeComponent(props: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const selectionBase = getSelectionBase(location.pathname)

  const [items, setItems] = React.useState<SelectableTrack[]>(
    props.editedTrack.id &&
      props.selectedTracks.some(t => t.id === props.editedTrack.id)
      ? props.selectedTracks.map(track => {
          return {
            ...track,
            isSelected: track.id === props.editedTrack.id,
            isOriginalFormatSelected: track.id === props.originalFormatTrackId,
          }
        })
      : props.selectedTracks.map((track, index) => {
          return { ...track, isSelected: index === 0 }
        }),
  )

  const loadData = React.useCallback(async () => {
    const chosenTrack = items.find(item => item.isSelected)

    if (chosenTrack && props.editedTrack.id !== chosenTrack.id) {
      updateMetadata(chosenTrack)
    }
  }, [items, props.editedTrack.id])

  React.useEffect(() => {
    if (props.mergeConfirmed) onTrackMerge()
  }, [props.mergeConfirmed])

  const updateMetadata = React.useCallback(
    (chosenTrack: Track) => {
      props.initEditedTrack({
        id: chosenTrack.id,
        url: chosenTrack.url,
        metadata: {
          ...getCommonBasicMetadataFields([
            {
              artwork: get(
                chosenTrack,
                'artwork_sizes.full',
                chosenTrack.artwork,
              ),
              name: chosenTrack.name,
              album: chosenTrack.album,
              artist: chosenTrack.artist,
            },
          ]),
          ...getCommonExtraMetadataFields([chosenTrack.metadata]),
        },
        comments: [],
        groups: chosenTrack.groups,
      }) // props.fetchTrackMetadata(chosenTrack.id)
    },
    [props.initEditedTrack],
  )

  const returnPath = getSelectionReturnPath(location.pathname)
  const onClose = React.useCallback(() => {
    props.resetTrackMergeForm()
    navigate(returnPath)
  }, [props.resetTrackMergeForm, navigate, returnPath])

  const onTrackMerge = React.useCallback(() => {
    const masterTrack = items.find(t => t.isSelected)
    const tracksToMerge = items.filter(t => !t.isSelected)
    const losslessFormatTrack = items.find(t => t.isOriginalFormatSelected)
    props.mergeTracks(
      masterTrack,
      tracksToMerge,
      losslessFormatTrack,
      returnPath,
    )
  }, [items, props.mergeTracks, returnPath])

  const onTrackSelect = React.useCallback(
    (track: Track, selectedIndex: number) => {
      setItems(prevItems =>
        prevItems.map((item, index) => {
          return { ...item, isSelected: index === selectedIndex }
        }),
      )

      updateMetadata(track)
    },
    [updateMetadata],
  )

  const onOriginalFormatSelect = React.useCallback(
    (track: Track, toggleIndex: number) => {
      setItems(prevItems =>
        prevItems.map((item, index) => {
          const selectableItem = item as SelectableTrack
          return {
            ...selectableItem,
            isOriginalFormatSelected: !!(
              index === toggleIndex && !selectableItem.isOriginalFormatSelected
            ),
          }
        }),
      )
      props.selectOriginalFormatInTrackMergeForm(track.id)
    },
    [props.selectOriginalFormatInTrackMergeForm],
  )

  const chosenTrack = items.find(item => item.isSelected)
  const error =
    props.mergeStatus === 'error' ? (
      <p className="EditMetadata_Error">Sorry, an error occurred</p>
    ) : null
  const mergeButton =
    props.settings.hide_merge_warning || props.mergeConfirmed ? (
      <Button
        onClick={onTrackMerge}
        caption="Merge tracks"
        progressCaption="Merging tracks"
        progressIndicator={props.mergeStatus === 'merging'}
      />
    ) : (
      <Button link={`${selectionBase}/confirm-merge`} caption="Merge tracks" />
    )
  const origExtensions = getListOfOriginalFormats(items as any as Track[])
  const mergeMessage = `Some of the tracks you're trying to merge have
    lossless
    (${origExtensions.join(', ')}) formats
    attached. If you'd like to pass across a lossless format to the master
    track, select it via the checkboxes below.`
  return (
    <div>
      <DocumentHeadTags title="Merge tracks | DISCO" />
      <DiscoAsyncModal
        style={doubleScrollModalStyles}
        fallbackTitle="Merge tracks"
        onRequestClose={onClose}
        loadData={loadData}
        render={() => (
          <div className="BulkEditor">
            <DocumentHeadTags title="Merge tracks | DISCO" />
            <div className="BulkEditor_MergeCont">
              <div>
                <h1 className="Form_Header">
                  Merge
                  <HelpTooltip id="tooltip_track_merge_modal_header" />
                </h1>
                <MetadataFields
                  role="merge"
                  track={chosenTrack}
                  editedTrack={props.editedTrack}
                  updateEditedTrack={props.updateEditedTrack}
                  showExtraMetadata
                  hideMoreButton={props.hideMoreButton}
                />
                {error}
              </div>
              <div className="BulkEditor_List">
                <div className="BulkEditor_ListHeader">
                  <label>Please select the master track</label>
                  <HelpTooltip id="tooltip_track_merge_modal_master_track" />
                </div>
                {origExtensions.length > 0 && (
                  <FormNote
                    className="BulkEditor_ListSubHeader"
                    note={mergeMessage}
                  />
                )}
                <TrackList
                  items={items}
                  onTrackSelect={onTrackSelect}
                  onOriginalFormatSelect={onOriginalFormatSelect}
                  merge
                />
              </div>
            </div>
            <DialogFooter primary={mergeButton} onClose={onClose} />
          </div>
        )}
      />
    </div>
  )
}

const TrackMerge = connector(TrackMergeComponent)
export default TrackMerge
